'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useConnection, useChainId } from 'wagmi';
import { Card, Button, Tabs, Tab, Select, SelectItem } from '@heroui/react';
import { shibarium, dogechain } from 'viem/chains';
import DirectListings from '@/components/marketplace/DirectListings';
import EnglishAuctions from '@/components/marketplace/EnglishAuctions';
import Collections from '@/components/marketplace/Collections';
import { MARKETPLACE_CONTRACTS } from '@/constants/marketplace/contracts';
import { getMarketplaceCollections } from '@/constants/marketplace/collections';
import { useRouter, useSearchParams } from 'next/navigation';
import { usePortfolioOpen } from '@/hooks/modals/usePortfolioOpen';

// This list dictates which chains appear in the marketplace UI.
// It should be kept in sync with chains that have valid, deployed marketplace contracts
// defined in `apps/web/constants/marketplace/contracts.ts`.
const SUPPORTED_CHAINS = [{
  id: shibarium.id,
  name: 'Shibarium'
}, {
  id: dogechain.id,
  name: 'Dogechain'
}, {
  id: dogechain.id,
  name: 'DogeOS (Coming Soon)'
}];
export default function MarketplaceClient() {
  const $ = _c(36);
  const {
    isConnected
  } = useConnection();
  useChainId();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    onOpen: onPortfolioOpen
  } = usePortfolioOpen();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("tab");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tabParam = t0;
  let t1;
  let t2;
  if ($[2] !== searchParams) {
    const chainParam = searchParams.get("chain");
    t1 = useState;
    t2 = chainParam ? Number.parseInt(chainParam, 10) : shibarium.id;
    $[2] = searchParams;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const [selectedChain, setSelectedChain] = t1(t2);
  const [activeTab, setActiveTab] = useState(tabParam || "listings");
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = tab => {
      setActiveTab(tab);
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleTabChange = t3;
  let t4;
  let t5;
  if ($[6] !== activeTab || $[7] !== router || $[8] !== searchParams || $[9] !== selectedChain) {
    t4 = () => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("tab", activeTab);
      params.set("chain", selectedChain.toString());
      router.replace(`/marketplace?${params.toString()}`, {
        scroll: false
      });
    };
    t5 = [selectedChain, activeTab, router, searchParams];
    $[6] = activeTab;
    $[7] = router;
    $[8] = searchParams;
    $[9] = selectedChain;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== selectedChain) {
    t6 = MARKETPLACE_CONTRACTS.some(contract => contract.chain.id === selectedChain);
    $[12] = selectedChain;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const hasMarketplace = t6;
  getMarketplaceCollections(selectedChain);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div><h1 className="text-4xl font-bold mb-2">NFT Marketplace</h1><p className="text-neutral-400">Buy, sell, and auction NFTs across multiple chains</p></div>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== selectedChain) {
    t8 = selectedChain.toString();
    $[15] = selectedChain;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = [t8];
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = keys => setSelectedChain(Number.parseInt(Array.from(keys)[0] as string, 10));
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = SUPPORTED_CHAINS.map(_temp);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t9) {
    t12 = <Select selectedKeys={t9} onSelectionChange={t10} aria-label="Select blockchain" placeholder="Select chain" className="w-48" size="sm">{t11}</Select>;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== isConnected || $[24] !== onPortfolioOpen) {
    t13 = isConnected && <Button onPress={() => onPortfolioOpen("nfts")}>My NFTs</Button>;
    $[23] = isConnected;
    $[24] = onPortfolioOpen;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">{t7}<div className="flex items-center gap-4">{t12}{t13}</div></div>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== activeTab || $[30] !== hasMarketplace || $[31] !== selectedChain) {
    t15 = !hasMarketplace ? <Card className="p-8 text-center"><h2 className="text-2xl font-semibold mb-4">No Marketplace Deployed</h2><p>There is no marketplace contract deployed on {SUPPORTED_CHAINS.find(chain_0 => chain_0.id === selectedChain)?.name} yet.</p></Card> : <div className="w-full"><Tabs selectedKey={activeTab} onSelectionChange={key => handleTabChange(key as string)} className="w-full" variant="underlined" color="secondary"><Tab key="listings" title="Listings"><DirectListings chainId={selectedChain} /></Tab><Tab key="collections" title="Collections"><Collections chainId={selectedChain} /></Tab><Tab key="auctions" title="Auctions"><EnglishAuctions chainId={selectedChain} /></Tab></Tabs></div>;
    $[29] = activeTab;
    $[30] = hasMarketplace;
    $[31] = selectedChain;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <div className="container mx-auto py-10 px-4">{t14}{t15}</div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}
function _temp(chain) {
  return <SelectItem key={chain.id.toString()}>{chain.name}</SelectItem>;
}